<template>
  <LightBox ref="wrapper" class="form pa-0">
    <Stepper
      :steps="steps.map(s => $tt(`form_steps.${s}`))"
      :step="currentStep"
      :disabled
      :loading
      @next="next"
      @back="emit('back')"
    >
      <v-form :ref="setFormRef">
        <FormSaveAndResume :form @resume="emit('resume')" />
        <slot :name="steps[currentStep - 1]" />
        <slot name="footer" />
      </v-form>
    </Stepper>

    <DesktopOnly>
      <BackToFormBtn :visible="formAboveView" @click="goTop" />
    </DesktopOnly>
    <RevisionConflictModal v-model="revisionConflict" @refresh="resumeAfterRevisionConflict" />
  </LightBox>
</template>

<script setup lang="ts">
import { inViewport } from '~/helpers/general';
import type { FormStep, ResumeStatus, VisaForm } from '~/types';
import { snackbarInjectionKey } from '~/assets/injection-keys';
import { useEventListener } from '@vueuse/core';

const snackbar = inject(snackbarInjectionKey)!;

const props = defineProps<{
  currentStep: number;
  form: VisaForm;
  steps: FormStep[];
  loading: boolean;
  disabled?: boolean;
}>();

const wrapper = useTemplateRef('wrapper');

const { setFormRef, submit, invalidItems } = useForm();
const { formValidationFailedBus } = useEmitter();
const { resumeStatus } = useFormResume(props.form.country);
const { revisionConflict } = useFormSave(props.form);

useEventListener('popstate', onPopState);
const { formAboveView } = useFormIntersection(wrapper);

watch(() => props.currentStep, onStepChange);
watch(resumeStatus, handleResume);

const emit = defineEmits(['next', 'back', 'resume']);

function handleResume(status: ResumeStatus) {
  if (status == 'non_resumable') return;
  if (status == 'success') {
    snackbar.value!.showSuccess('may_resume');
  } else if (status == 'error') {
    snackbar.value!.showError('resume_fail');
  }
  goTop();
}

function next() {
  if (props.disabled) return;
  submit(() => emit('next'), { activeValidation: true, onInvalid });
}

function onInvalid() {
  const [invalidItem] = invalidItems.value;
  if (!invalidItem) return;
  const el = document.getElementById(`${invalidItem.id}`) || document.querySelector(`[name="${invalidItem.id}"]`);
  if (!el) return;
  formValidationFailedBus.emit(el);
  if (!inViewport(el)) scrollToField(el);
}

function scrollToField(el: HTMLElement) {
  setTimeout(() => el.scrollIntoView(), 50); // wait for panel expansion
}

function goTop() {
  wrapper.value!.$el.scrollIntoView();
}

function onPopState() {
  if (props.currentStep > 1) {
    emit('back');
  }
}

function onStepChange(step: number) {
  goTop();
  window.history.pushState({ step }, '');
}

function resumeAfterRevisionConflict() {
  emit('resume');
  revisionConflict.value = false;
}
</script>

<style lang="scss" scoped>
.form {
  width: 896px;
  position: relative;
  max-width: 100%;
}
.v-form {
  padding: var(--space-6) var(--space-4) var(--space-6) var(--space-4);
}

@media (max-width: 850px) {
  .v-form {
    padding: var(--space-5) var(--space-2) var(--space-5) var(--space-2);
  }
}
</style>